import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, ScanLine, X } from 'lucide-react';
import PhotoLightbox from './PhotoLightbox';

// Persian digits for the index badge. Sized to MAX_PHOTOS (8). If MAX_PHOTOS
// grows past 8, append more digits here AND in ConstellationBanner.jsx.
const FA_DIGITS = ['۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸'];

function fmtCoord(n) {
  return n.toFixed(5);
}

/**
 * Single evidence card. Used both in the upload gallery (interactive, with
 * remove button) and in the verdict view (compact, readOnly).
 *
 * Visual states driven by props:
 *   - extracting=true  → amber "reading stamp…" overlay over the photo
 *   - isOffender=true  → red ring + persistent "تعویض کنید" callout
 *   - coords.source==='stamp' → 📎 icon (vs 📍 for EXIF) on the coord chip
 */
export default function EvidenceCard({
  entry,                  // { file, previewUrl }
  index,                  // 0-based
  coords,                 // { lat, lng, source?, accuracy_m?, note? } | null | undefined
  uploadProgress,         // 0..1 | null
  onRemove,               // () => void | null (null = readOnly)
  size = 'normal',        // 'normal' | 'compact'
  extracting = false,     // server is reading the stamp from THIS photo right now
  isOffender = false,     // mismatch: this photo is the outlier — user should swap it
  offenderReason = null,  // short Persian sentence shown next to the offender ring
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const dim = size === 'compact' ? 120 : 180;
  const stampSource = coords?.source === 'stamp';

  const cardClass = [
    'evidence-card',
    `evidence-card-${size}`,
    isOffender ? 'evidence-card-offender' : '',
    extracting ? 'evidence-card-extracting' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <motion.div
        className={cardClass}
        style={{ width: dim, height: dim }}
        initial={{ opacity: 0, x: 16 }}
        animate={
          isOffender
            ? { opacity: 1, x: 0, scale: [1, 1.02, 1] }
            : { opacity: 1, x: 0 }
        }
        exit={{ opacity: 0, scaleX: 0, width: 0 }}
        transition={
          isOffender
            ? { duration: 0.6, ease: 'easeInOut', scale: { duration: 0.6, repeat: 1 } }
            : { duration: 0.22, ease: 'easeOut' }
        }
        layout
      >
        <img
          src={entry.previewUrl}
          alt={`عکس بازرسی ${FA_DIGITS[index]}`}
          onClick={() => setLightboxOpen(true)}
        />
        <span className="evidence-card-badge" aria-label={`شماره ${FA_DIGITS[index]}`}>
          {FA_DIGITS[index]}
        </span>
        {onRemove && (
          <button
            type="button"
            className="evidence-card-remove"
            onClick={onRemove}
            aria-label={`حذف عکس ${FA_DIGITS[index]}`}
          >
            <X size={14} />
          </button>
        )}

        {/* Extraction in flight — amber overlay with a scanning-line motif */}
        {extracting && (
          <div className="evidence-card-extracting-overlay" aria-live="polite">
            <ScanLine size={18} className="evidence-extracting-icon" />
            <span>در حال خواندن مهر…</span>
          </div>
        )}

        {coords && (
          <span
            className={`evidence-card-coord${stampSource ? ' evidence-card-coord-stamp' : ''}`}
            title={stampSource
              ? 'از مهرِ روی تصویر استخراج شد (EXIF نداشت)'
              : 'از EXIF تصویر خوانده شد'}
          >
            {stampSource ? <ScanLine size={11} aria-hidden /> : <MapPin size={11} aria-hidden />}
            <span dir="ltr">{fmtCoord(coords.lat)}, {fmtCoord(coords.lng)}</span>
          </span>
        )}

        {typeof uploadProgress === 'number' && uploadProgress < 1 && (
          <span
            className="evidence-card-progress"
            style={{ width: `${Math.round(uploadProgress * 100)}%` }}
          />
        )}

        {/* Offender callout — visible on the gallery card itself, not just
            in the constellation banner. User sees "swap THIS one". */}
        {isOffender && (
          <div className="evidence-card-offender-callout">
            <span className="evidence-offender-pulse" aria-hidden />
            <span>{offenderReason || 'این عکس را تعویض کنید'}</span>
          </div>
        )}
      </motion.div>
      <PhotoLightbox
        open={lightboxOpen}
        src={entry.previewUrl}
        alt={`عکس بازرسی ${FA_DIGITS[index]}`}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}

